import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { formatDecimal } from '../../../../../../services/utils/format-numbers'
import { bffService } from '../../../../../../services/utils/axios'
import { formatDate } from '../../../components/formatDate'

/**
 * ProducerConfirmUserDetails Component
 *
 * This component displays a confirmation view of all producer-specific contract details
 * before final submission. It organizes information into logical sections including:
 * - Contract information
 * - Personal information
 * - Company registration (producer-specific)
 * - Billing information
 * - Banking details
 * - Plant details (producer-specific, e.g., power plant type, installed power)
 * - Stadtwerke information (producer-specific)
 * - Tax and delivery information
 * - Terms and marketing consent
 *
 * The component also handles the API call to update the contract when confirmed.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.payload - The contract data to display for confirmation
 * @param {Function} props.openFeedback - Function to display success feedback after successful update
 * @param {number} props.submit - Trigger for API submission (incremented to trigger)
 * @param {Function} props.setError - Function to display error feedback if update fails
 * @param {Function} props.setSubmit - Function to reset the submit counter
 * @param {Function} props.refetch - Function to refresh contract data after successful update
 *
 * @example
 * <ProducerConfirmUserDetails
 *   payload={contractData}
 *   openFeedback={() => setFeedback(true)}
 *   submit={submitCounter}
 *   setError={() => setErrorState(true)}
 *   setSubmit={setSubmitCounter}
 *   refetch={refreshData}
 * />
 *
 * @returns {React.ReactElement} A confirmation view of producer contract details
 */
function ProducerConfirmUserDetails({
  payload,
  openFeedback,
  submit,
  setError,
  setSubmit,
  refetch,
}) {
  const { t } = useTranslation()

  useEffect(() => {
    if (submit > 0) updateContractDetails()
    setSubmit(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit])

  /**
   * This api call is used to update the single contract data using PATCH /contracts/{uuid}.
   * It returns the updated contract data if successful.
   */
  const updateContractDetails = async () => {
    try {
      const response = await bffService.patch(
        `/contracts/${payload?.uuid}`,
        payload
      )

      if (response.data) {
        refetch()
        openFeedback()
      } else {
        setError(true)
      }
    } catch (error) {
      console.error('Error updating contract:', error)
      setError(true)
    }
  }

  let plantAddressDescription = ''
  if (payload?.isPlantAddressSameAsAddress) {
    plantAddressDescription =
      payload?.address?.postalCode + ' ' + payload?.address?.city
  } else {
    plantAddressDescription =
      payload?.plantAddress?.postalCode + ' ' + payload?.plantAddress?.city
  }

  let billingAddressName = ''
  let billingAddressStreet = ''
  let billingAddressHouseNumber = ''
  let billingAddressPostalCode = ''
  let billingAddressCity = ''
  if (payload?.isBillingAddressSameAsAddress) {
    if (payload?.salutation === 'COMPANY') {
      billingAddressName = payload?.companyName
    } else {
      billingAddressName = payload?.firstName + ' ' + payload?.lastName
    }
    billingAddressStreet = payload?.address?.street
    billingAddressHouseNumber = payload?.address?.houseNumber
    billingAddressPostalCode = payload?.address?.postalCode
    billingAddressCity = payload?.address?.city
  } else {
    billingAddressName = payload?.billingAddress?.name
    billingAddressStreet = payload?.billingAddress?.street
    billingAddressHouseNumber = payload?.billingAddress?.houseNumber
    billingAddressPostalCode = payload?.billingAddress?.postalCode
    billingAddressCity = payload?.billingAddress?.city
  }

  return (
    <>
      {/* Contract Information */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLContractsNumber')}</Typography>
        <Typography variant="body1" align="right" id="contractNumber">
          {payload?.contractNumber}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLContractType')}</Typography>
        <Typography variant="body1" align="right" id="contractType">
          {t('LBLProducer')}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLContaractStatus')}</Typography>
        <Typography variant="body1" align="right" id="contractStatus">
          {payload?.contractStatus === 'ACTIVE'
            ? t('LBLActive')
            : payload?.contractStatus === 'INACTIVE'
            ? t('LBLInactive')
            : t('LBLInProgress')}
        </Typography>
      </Stack>

      {/* Personal Information */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">Name</Typography>
        <Typography variant="body1" align="right" id="fullName">
          {t(payload?.salutation) +
            ' ' +
            (payload?.firstName && payload?.lastName
              ? `${payload.firstName} ${payload.lastName}`
              : '')}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLAddress')}</Typography>
        <Stack my={2}>
          <Typography variant="body1" align="right" id="address1">
            {payload?.address?.street +
              ' ' +
              payload?.address?.houseNumber +
              ','}
          </Typography>
          <Typography variant="body1" align="right" id="address2">
            {payload?.address?.postalCode + ' ' + payload?.address?.city}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">E-Mail</Typography>
        <Typography variant="body1" align="right" id="email">
          {payload?.email}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLHandy')}</Typography>
        <Typography variant="body1" align="right" id="phoneNumber">
          {payload?.phone}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLBirthDate')}</Typography>
        <Typography variant="body1" align="right" id="dateOfBirth">
          {formatDate(payload?.dateOfBirth)}
        </Typography>
      </Stack>

      {/* Company Registration Information - Producer Specific */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLRegisterCourt')}</Typography>
        <Typography variant="body1" align="right" id="registeredCourt">
          {payload?.registeredCourt}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLRegistrationNumber')}</Typography>
        <Typography variant="body1" align="right" id="registrationNumber">
          {payload?.registrationNumber}
        </Typography>
      </Stack>

      {/* Billing Information */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLBillingAddressOwner')}</Typography>
        <Typography variant="body1" align="right" id="billingOwner">
          {billingAddressName}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLBillingAddress')}</Typography>
        <Stack my={2}>
          <Typography variant="body1" align="right" id="billingAddress1">
            {billingAddressStreet + ' ' + billingAddressHouseNumber + ','}
          </Typography>
          <Typography variant="body1" align="right" id="billingAddress2">
            {billingAddressPostalCode + ' ' + billingAddressCity}
          </Typography>
        </Stack>
      </Stack>

      {/* Banking Information */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLBankName')}</Typography>
        <Typography variant="body1" align="right" id="creditInstituteName">
          {payload?.paymentDetails?.creditInstituteName}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLBic')}</Typography>
        <Typography variant="body1" align="right" id="bic">
          {payload?.paymentDetails?.bic}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLIban')}</Typography>
        <Typography variant="body1" align="right" id="iban">
          {payload?.paymentDetails?.iban}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLAccountHolder')}</Typography>
        <Typography variant="body1" align="right" id="accountHolderName">
          {payload?.paymentDetails?.accountHolderName}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLAcceptCondition')}</Typography>
        <Typography variant="body1" align="right" id="bankAccept">
          {payload?.paymentDetails?.sepaAccepted
            ? t('UserDetailSelected')
            : t('UserDetailNotSelected')}
        </Typography>
      </Stack>

      {/* Plant Details - Producer Specific */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLPlantName')}</Typography>
        <Typography variant="body1" align="right" id="plantName">
          {payload?.plantName}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLPlantType')}</Typography>
        <Typography variant="body1" align="right" id="plantType">
          {t(payload?.energyPlantType)}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLPlantAdress')}</Typography>
        <Stack my={2}>
          <Typography variant="body1" align="right" id="plantAddress">
            {plantAddressDescription}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLInstalledOutput')}</Typography>
        <Typography variant="body1" align="right" id="installedPowerKw">
          {formatDecimal(payload?.installedPowerKw ?? 0)}
        </Typography>
      </Stack>

      {payload?.commissioningDate &&
        payload?.commissioningDate !== '1970-01-01' && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            my={4}
          >
            <Typography variant="body1">{t('LBLCommissioningDate')}</Typography>
            <Typography variant="body1" align="right" id="commissioningDate">
              {formatDate(payload?.commissioningDate)}
            </Typography>
          </Stack>
        )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLMarketLocation')}</Typography>
        <Typography variant="body1" align="right" id="marketLocation">
          {payload?.marketLocation}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLMeterNumber')}</Typography>
        <Typography variant="body1" align="right" id="meterNumber">
          {payload?.meterNumber}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLPlantKey')}</Typography>
        <Typography variant="body1" align="right" id="plantKey">
          {payload?.plantKey}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLOperator')}</Typography>
        <Typography variant="body1" align="right" id="gridOperator">
          {payload?.gridOperator}
        </Typography>
      </Stack>

      {/* Stadtwerke - Producer Specific */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLStadtWerkeKunden')}</Typography>
        <Typography variant="body1" align="right" id="isAlreadyClientOfUtility">
          {payload?.isAlreadyClientOfUtility
            ? t('UserDetailSelected')
            : t('UserDetailNotSelected')}
        </Typography>
      </Stack>

      {!payload?.isAlreadyClientOfUtility && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          my={4}
        >
          <Typography variant="body1">{t('LBLStadtWerkeKundenNr')}</Typography>
          <Typography variant="body1" align="right" id="utilityContractNumber">
            {payload?.utilityContractNumber}
          </Typography>
        </Stack>
      )}

      {/* Tax and Start of Delivery */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLSalesTaxLiability')}</Typography>
        <Typography variant="body1" align="right" id="isSalesTaxLiable">
          {payload?.isSalesTaxLiable
            ? t('UserDetailSelected')
            : t('UserDetailNotSelected')}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLStartOfDeliveryDate')}</Typography>
        <Typography variant="body1" align="right" id="startOfDeliveryDate">
          {payload?.isStartOfDeliveryNextPossibleDate
            ? t('NextPossibleDate')
            : formatDate(payload?.startOfDeliveryDate)}
        </Typography>
      </Stack>

      {/* Terms and Marketing */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLAgb')}</Typography>
        <Typography variant="body1" align="right" id="agb">
          {payload?.isTermsAndConditionsAccepted
            ? t('UserDetailSelected')
            : t('UserDetailNotSelected')}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLMarketing')}</Typography>
        <Typography variant="body1" align="right" id="marketing">
          {payload?.isMarketingInformationConsentGiven
            ? t('UserDetailSelected')
            : t('UserDetailNotSelected')}
        </Typography>
      </Stack>
    </>
  )
}

ProducerConfirmUserDetails.propTypes = {
  payload: PropTypes.objectOf(PropTypes.any).isRequired,
  openFeedback: PropTypes.func.isRequired,
  submit: PropTypes.number.isRequired,
  setError: PropTypes.func.isRequired,
  setSubmit: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
}

export default ProducerConfirmUserDetails
